(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- body scroll lock ---------- */
  let scrollY = 0;
  let bodyLocked = false;
  function lockBody() {
    if (bodyLocked) return;
    scrollY = window.scrollY;
    document.body.classList.add('is-locked');
    document.body.style.top = `-${scrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    bodyLocked = true;
  }
  function unlockBody() {
    if (!bodyLocked) return;
    document.body.classList.remove('is-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    bodyLocked = false;
    window.scrollTo(0, scrollY);
  }

  /* ---------- drawers (mobile menu + cart) ---------- */
  function overlayFor(id) {
    return document.querySelector(`[data-overlay-for="${id}"]`);
  }

  // Overlay visibility + body lock are DERIVED from the live DOM state of the
  // drawers, so they can never get "stuck" — e.g. when the cart refresh replaces
  // the drawer node, or an external widget (Microsoft Clarity, etc.) manipulates
  // the page. There is no separate in-memory list of open drawers to desync.
  function syncDrawerState() {
    let anyOpen = false;
    document.querySelectorAll('[data-drawer]').forEach((d) => {
      const isOpen = d.getAttribute('aria-hidden') === 'false';
      if (isOpen) anyOpen = true;
      const overlay = overlayFor(d.id);
      if (overlay) overlay.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    });
    if (anyOpen) lockBody();
    else unlockBody();
  }

  function openDrawer(drawerEl) {
    if (!drawerEl) return;
    drawerEl.setAttribute('aria-hidden', 'false');
    syncDrawerState();
    const focusable = drawerEl.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function closeDrawer(drawerEl) {
    if (!drawerEl) return;
    drawerEl.setAttribute('aria-hidden', 'true');
    syncDrawerState();
    // reset drill-down menu to its root once the drawer has slid away
    const mnav = drawerEl.querySelector('[data-mobile-nav]');
    if (mnav && mnav.__resetMnav) setTimeout(mnav.__resetMnav, 380);
  }

  function closeAllDrawers() {
    document.querySelectorAll('[data-drawer]').forEach((d) => d.setAttribute('aria-hidden', 'true'));
    syncDrawerState();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(document.getElementById(opener.getAttribute('data-drawer-open')));
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.closest('[data-drawer]'));
      return;
    }
    if (e.target.closest('.drawer-overlay')) closeAllDrawers();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.querySelector('[data-drawer][aria-hidden="false"]')) closeAllDrawers();
  });

  // Safety net: if the page is restored from bfcache or regains visibility with a
  // stale locked/overlay state, re-derive everything from the DOM.
  window.addEventListener('pageshow', syncDrawerState);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) syncDrawerState(); });

  /* ---------- mobile drill-down menu ---------- */
  document.querySelectorAll('[data-mobile-nav]').forEach((nav) => {
    const panels = {};
    nav.querySelectorAll('[data-mnav-panel]').forEach((p) => {
      panels[p.getAttribute('data-mnav-panel')] = p;
    });
    let stack = ['root'];
    const apply = () => {
      const active = stack[stack.length - 1];
      Object.keys(panels).forEach((id) => {
        const isActive = id === active;
        panels[id].classList.toggle('is-active', isActive);
        panels[id].setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      nav.scrollTop = 0;
    };
    nav.addEventListener('click', (e) => {
      const to = e.target.closest('[data-mnav-to]');
      if (to && nav.contains(to)) {
        e.preventDefault();
        stack.push(to.getAttribute('data-mnav-to'));
        apply();
        return;
      }
      const back = e.target.closest('[data-mnav-back]');
      if (back && nav.contains(back)) {
        e.preventDefault();
        if (stack.length > 1) stack.pop();
        apply();
      }
    });
    nav.__resetMnav = () => { stack = ['root']; apply(); };
  });

  /* ---------- shine sweep on click ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn');
    if (!btn || btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true') return;
    if (prefersReducedMotion) return;
    btn.classList.remove('is-shining');
    void btn.offsetWidth;
    btn.classList.add('is-shining');
    setTimeout(() => btn.classList.remove('is-shining'), 760);
  }, true);

  /* ---------- quantity stepper ---------- */
  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-qty-decrease]');
    const inc = e.target.closest('[data-qty-increase]');
    if (!dec && !inc) return;
    const wrap = (dec || inc).closest('.qty');
    if (!wrap) return;
    const input = wrap.querySelector('input');
    if (!input) return;
    let v = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10);
    if (inc) v += 1;
    if (dec) v -= 1;
    if (v < min) v = min;
    if (max && v > max) v = max;
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- product gallery dots (mobile swipe) ---------- */
  $$('.pdp__gallery').forEach((gallery) => {
    const slides = $$('.pdp__slide', gallery);
    const dotsWrap = gallery.parentElement?.querySelector('.pdp__dots');
    if (slides.length < 2 || !dotsWrap) return;
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Image ${i + 1}`);
      if (i === 0) dot.setAttribute('aria-current', 'true');
      dot.addEventListener('click', () => {
        slides[i].scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      });
      dotsWrap.appendChild(dot);
    });
    let raf = null;
    gallery.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const w = gallery.clientWidth;
        const i = Math.round(gallery.scrollLeft / w);
        $$('button', dotsWrap).forEach((d, idx) => {
          if (idx === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      });
    }, { passive: true });
  });

  /* ---------- product form / variants / add to cart ---------- */
  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  class ProductForm {
    constructor(form) {
      this.form = form;
      const jsonEl = form.querySelector('[data-product-json]');
      this.product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      if (!this.product) return;
      this.idInput = form.querySelector('input[name="id"]');
      this.submit = form.querySelector('[type="submit"]');
      this.priceEls = $$('[data-pdp-price], [data-sticky-price]');
      this.stickyAdd = document.querySelector('[data-sticky-add]');
      this.optionInputs = $$('input[data-option-position]', form);
      this.gallery = form.closest('[id^="shopify-section-"]')?.querySelector('.pdp__gallery');
      const vmEl = form.querySelector('[data-variant-media]');
      this.variantMedia = vmEl ? JSON.parse(vmEl.textContent) : {};
      this.bind();
      this.update();
    }
    bind() {
      this.optionInputs.forEach((i) => i.addEventListener('change', () => this.update(true)));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    showVariantMedia(variant) {
      if (!variant || !this.gallery) return;
      const mediaId = this.variantMedia[variant.id];
      if (!mediaId) return;
      // Only the mobile carousel scrolls horizontally; on desktop the images are stacked.
      if (this.gallery.scrollWidth <= this.gallery.clientWidth + 4) return;
      const slide = this.gallery.querySelector('.pdp__slide[data-media-id="' + mediaId + '"]');
      if (slide) slide.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
    }
    pickedOptions() {
      const picked = {};
      this.optionInputs.forEach((i) => {
        if (i.checked) picked[i.dataset.optionPosition] = i.value;
      });
      return picked;
    }
    findVariant() {
      const picked = this.pickedOptions();
      const keys = Object.keys(picked);
      // No option inputs rendered (single default variant) → use the only variant
      if (keys.length === 0) return this.product.variants[0];
      return this.product.variants.find((v) =>
        keys.every((idx) => v.options[idx] === picked[idx])
      );
    }
    update(scrollMedia = false) {
      const variant = this.findVariant();
      // Reflect selected option labels
      this.optionInputs.forEach((i) => {
        if (i.checked) {
          const group = i.closest('.variant-group');
          const valEl = group?.querySelector('.variant-group__value');
          if (valEl) valEl.textContent = i.value;
        }
      });
      if (!variant) {
        if (this.submit) {
          this.submit.setAttribute('disabled', '');
          const txt = this.submit.querySelector('[data-submit-text]');
          if (txt) txt.textContent = this.submit.dataset.unavailableText || 'Unavailable';
        }
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      if (scrollMedia) this.showVariantMedia(variant);
      this.priceEls.forEach((el) => {
        const pct = parseFloat(el.getAttribute('data-promo')) || 0;
        if (pct > 0) {
          const sale = Math.round((variant.price * (100 - pct)) / 100);
          el.innerHTML =
            '<span class="price"><span class="price__sale">' + formatMoney(sale) +
            '</span> <span class="price__compare">' + formatMoney(variant.price) +
            '</span> <span class="price__badge price__badge--promo">-' + pct + '%</span></span>';
        } else {
          el.innerHTML = formatMoney(variant.price);
        }
      });
      if (this.submit) {
        const txt = this.submit.querySelector('[data-submit-text]');
        if (variant.available) {
          this.submit.removeAttribute('disabled');
          if (txt) txt.textContent = this.submit.dataset.addText || 'Add to cart';
          if (this.stickyAdd) { this.stickyAdd.removeAttribute('disabled'); this.stickyAdd.textContent = this.submit.dataset.addText || 'Add to cart'; }
        } else {
          this.submit.setAttribute('disabled', '');
          if (txt) txt.textContent = this.submit.dataset.soldText || 'Sold out';
          if (this.stickyAdd) { this.stickyAdd.setAttribute('disabled', ''); this.stickyAdd.textContent = this.submit.dataset.soldText || 'Sold out'; }
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    async onSubmit(e) {
      e.preventDefault();
      const fd = new FormData(this.form);
      if (this.submit) this.submit.setAttribute('disabled', '');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Add to cart failed');
        await refreshCart();
        openDrawer(document.getElementById('CartDrawer'));
      } catch (err) {
        console.error(err);
      } finally {
        if (this.submit) this.submit.removeAttribute('disabled');
      }
    }
  }

  $$('[data-product-form]').forEach((el) => new ProductForm(el));

  /* ---------- cart refresh via Section Rendering API ---------- */
  async function refreshCart() {
    try {
      const sectionRes = await fetch('/?sections=cart-drawer', {
        headers: { Accept: 'application/json' },
      });
      const data = await sectionRes.json();
      const html = data['cart-drawer'];
      if (html) {
        const wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        const freshDrawer = wrap.querySelector('#CartDrawer');
        const current = document.getElementById('CartDrawer');
        if (freshDrawer && current) {
          const wasOpen = current.getAttribute('aria-hidden') === 'false';
          current.replaceWith(freshDrawer);
          if (wasOpen) freshDrawer.setAttribute('aria-hidden', 'false');
          syncDrawerState();
        }
      }
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (err) {
      console.error(err);
    }
  }

  /* ---------- cart line item changes ---------- */
  let debounceTimer;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    const key = input.getAttribute('data-cart-qty');
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        await refreshCart();
      } catch (err) {
        console.error(err);
      }
    }, 250);
  });

  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    const key = rm.getAttribute('data-cart-remove');
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      await refreshCart();
    } catch (err) {
      console.error(err);
    }
  });

  /* ---------- sticky add-to-cart bar ---------- */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  if (stickyBar) {
    const pForm = document.querySelector('[data-product-form]');
    const anchor = pForm && pForm.querySelector('.pdp__actions');
    const addBtn = stickyBar.querySelector('[data-sticky-add]');
    if (addBtn && pForm) {
      addBtn.addEventListener('click', () => {
        const submit = pForm.querySelector('[type="submit"]');
        if (submit && !submit.disabled) {
          if (pForm.requestSubmit) pForm.requestSubmit(submit);
          else submit.click();
        }
      });
    }
    if (anchor && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const past = !e.isIntersecting && e.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', past);
          stickyBar.setAttribute('aria-hidden', past ? 'false' : 'true');
        });
      }, { threshold: 0 });
      io.observe(anchor);
    }
  }

  /* ---------- share ---------- */
  document.addEventListener('click', (e) => {
    const share = e.target.closest('[data-share]');
    if (!share) return;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        const prev = share.textContent;
        share.textContent = 'Lien copié';
        setTimeout(() => { share.textContent = prev; }, 1800);
      });
    }
  });

  /* ---------- copy to clipboard (contact WhatsApp, etc.) ---------- */
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-copy]');
    if (!el) return;
    const text = el.getAttribute('data-copy');
    const valEl = el.querySelector('[data-copy-text]');
    const feedback = () => {
      el.classList.add('is-copied');
      if (valEl) {
        if (!valEl.dataset.orig) valEl.dataset.orig = valEl.textContent;
        valEl.textContent = el.dataset.copiedLabel || 'Copié';
      }
      setTimeout(() => {
        el.classList.remove('is-copied');
        if (valEl && valEl.dataset.orig) valEl.textContent = valEl.dataset.orig;
      }, 1800);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(feedback).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (err) {}
      document.body.removeChild(ta);
      feedback();
    }
  });

  /* ---------- image slider ---------- */
  const slideBehavior = prefersReducedMotion ? 'auto' : 'smooth';
  $$('[data-slider]').forEach((root) => {
    const track = root.querySelector('[data-slider-track]');
    if (!track) return;
    const slides = Array.from(track.children);
    const prev = root.querySelector('[data-slider-prev]');
    const next = root.querySelector('[data-slider-next]');
    const dotsWrap = root.querySelector('[data-slider-dots]');
    if (slides.length < 2) return;

    const goTo = (i) => {
      const idx = Math.max(0, Math.min(i, slides.length - 1));
      slides[idx].scrollIntoView({ behavior: slideBehavior, inline: 'start', block: 'nearest' });
    };
    const current = () => Math.round(track.scrollLeft / track.clientWidth);

    next && next.addEventListener('click', () => {
      const i = current();
      goTo(i >= slides.length - 1 ? 0 : i + 1);
    });
    prev && prev.addEventListener('click', () => {
      const i = current();
      goTo(i <= 0 ? slides.length - 1 : i - 1);
    });

    if (dotsWrap) {
      slides.forEach((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.setAttribute('aria-label', `${i + 1}`);
        if (i === 0) d.setAttribute('aria-current', 'true');
        d.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(d);
      });
      let raf = null;
      track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const i = current();
          Array.from(dotsWrap.children).forEach((d, idx) => {
            if (idx === i) d.setAttribute('aria-current', 'true');
            else d.removeAttribute('aria-current');
          });
        });
      }, { passive: true });
    }

    const sec = parseFloat(root.getAttribute('data-slider-autoplay'));
    if (sec && !prefersReducedMotion) {
      let timer = null;
      const start = () => {
        stop();
        timer = setInterval(() => {
          const i = current();
          goTo(i >= slides.length - 1 ? 0 : i + 1);
        }, sec * 1000);
      };
      const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
      start();
      root.addEventListener('pointerenter', stop);
      root.addEventListener('pointerleave', start);
      root.addEventListener('touchstart', stop, { passive: true });
    }
  });

  /* ---------- product carousel arrows ---------- */
  $$('[data-carousel]').forEach((scroller) => {
    const scope = scroller.closest('.container') || scroller.parentElement;
    const prev = scope && scope.querySelector('[data-slider-prev]');
    const next = scope && scope.querySelector('[data-slider-next]');
    const step = () => Math.max(scroller.clientWidth * 0.8, 240);
    next && next.addEventListener('click', () => scroller.scrollBy({ left: step(), behavior: slideBehavior }));
    prev && prev.addEventListener('click', () => scroller.scrollBy({ left: -step(), behavior: slideBehavior }));
  });

  /* ---------- header scroll state ---------- */
  const header = document.querySelector('.header');
  if (header) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- scroll reveal ---------- */
  const revealEls = $$('[data-reveal]');
  if (revealEls.length) {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealEls.forEach((el) => el.classList.add('is-visible'));
    } else {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      revealEls.forEach((el) => io.observe(el));
    }
  }

  /* ---------- size guide popup ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-sizeguide-open]');
    if (opener) {
      const dlg = document.getElementById(opener.getAttribute('data-sizeguide-open'));
      if (dlg) { if (typeof dlg.showModal === 'function') { dlg.showModal(); } else { dlg.setAttribute('open', ''); } }
      return;
    }
    const closer = e.target.closest('[data-sizeguide-close]');
    if (closer) {
      const dlg = closer.closest('[data-sizeguide]');
      if (dlg) { if (typeof dlg.close === 'function') { dlg.close(); } else { dlg.removeAttribute('open'); } }
      return;
    }
  });
  document.querySelectorAll('[data-sizeguide]').forEach((dlg) => {
    dlg.addEventListener('click', (e) => { if (e.target === dlg && typeof dlg.close === 'function') dlg.close(); });
  });

  /* ---------- rating → scroll to reviews ---------- */
  document.addEventListener('click', (e) => {
    const r = e.target.closest('[data-scroll-reviews]');
    if (!r) return;
    const target = document.querySelector('[data-reviews-section]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------- cart urgency timer ---------- */
  (function () {
    const KEY = 'mp_cart_timer_start';
    function remaining(durMs) {
      let start = parseInt(localStorage.getItem(KEY) || '0', 10);
      const now = Date.now();
      if (!start || now - start >= durMs) { start = now; try { localStorage.setItem(KEY, String(start)); } catch (e) {} }
      return durMs - (now - start);
    }
    function fmt(ms) {
      const s = Math.max(0, Math.floor(ms / 1000));
      const m = Math.floor(s / 60);
      const x = s % 60;
      return m + ':' + (x < 10 ? '0' : '') + x;
    }
    function tick() {
      const els = document.querySelectorAll('[data-cart-timer]');
      els.forEach((el) => {
        const dur = (parseInt(el.getAttribute('data-minutes'), 10) || 10) * 60000;
        const t = el.querySelector('[data-timer]');
        if (t) t.textContent = fmt(remaining(dur));
      });
    }
    setInterval(tick, 1000);
    tick();
  })();

  /* ---------- expose minimal API ---------- */
  window.MerveillesParis = { openDrawer, closeDrawer, refreshCart };
})();
